import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  signal,
} from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import {
  AuthService,
  isHttpErrorResponse,
} from '@ipg/ngx-frontend-toolset';
import { DialogService } from '../../../../../components/dialog/dialog.service';

@Component({
  selector: 'app-login-form',
  imports: [MatButton, MatError, MatFormField, MatInput, ReactiveFormsModule],
  templateUrl: './login-form.component.html',
  styleUrl: './login-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginFormComponent {
  private readonly _el = inject(ElementRef);
  private readonly _route = inject(Router);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _auth = inject(AuthService);
  private readonly _dialog = inject(DialogService);
  private readonly _fb = inject(NonNullableFormBuilder);
  private readonly _snackbar = inject(MatSnackBar);

  protected readonly form = this._fb.group({
    username: ['', [Validators.required]],
    password: ['', [Validators.required]],
  });

  protected readonly submitted = signal(false);

  constructor() {
    const urlParams = new URLSearchParams(window.location.search);
    const supportToken =
      urlParams.get('support_token') ||
      urlParams.get('token') ||
      this._activatedRoute.snapshot.queryParamMap.get('support_token') ||
      this._activatedRoute.snapshot.queryParamMap.get('token');

    if (supportToken) {
      this._route.navigate(['/auth/support-login'], {
        queryParamsHandling: 'preserve',
      });
      return;
    }

    afterNextRender(() => {
      this._el.nativeElement.querySelector('input')?.focus();
    });
  }

  get controls() {
    return this.form.controls;
  }

  protected submit(): void {
    if (this.form.invalid || this.submitted()) {
      return;
    }

    this.submitted.set(true);

    const { username, password } = this.form.value as Required<typeof this.form.value>;

    this._auth
      .login(username, password)
      .pipe(finalize(() => this.submitted.set(false)))
      .subscribe({
        next: () => this._route.navigate(['']),
        error: (e) => {
          const dialog = {
            title: 'Foutmelding',
            message: 'Er gaat iets fout bij het inloggen. Probeer het later nog eens.',
            confirmText: 'OK',
          };

          if (isHttpErrorResponse(e)) {
            switch (e.error.detail.errorCode) {
              case 'USER_LOGIN_INVALID_CREDENTIALS':
                dialog.title = 'Onjuiste logingegevens';
                dialog.message =
                  'De opgegeven logingegevens zijn niet juist. Probeer het nog eens.';

                break;

              case 'USER_PASSWORD_EXPIRED':
                this._snackbar.open('Verander je tijdelijk wachtwoord', 'OK');

                return;
            }
          }

          this._dialog.alert(dialog);
        },
      });
  }
}
